import AsyncStorage from '@react-native-async-storage/async-storage';
import type { ThemePreference } from './ThemeProvider';

const STORAGE_KEY = '@cache:theme-preference';

const VALID_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];

function isValidPreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (VALID_PREFERENCES as readonly string[]).includes(value);
}

/**
 * Load the user's stored theme preference from AsyncStorage. Returns `'system'`
 * if nothing is stored or the stored value is unreadable / invalid — that
 * matches the ThemeProvider default and means a fresh install follows the OS
 * setting until the user picks something explicit.
 */
export async function loadThemePreference(): Promise<ThemePreference> {
  try {
    const stored = await AsyncStorage.getItem(STORAGE_KEY);
    if (isValidPreference(stored)) return stored;
    return 'system';
  } catch (err) {
    console.warn('[theme] Failed to load theme preference, falling back to system:', err);
    return 'system';
  }
}

/**
 * Persist the user's theme preference. Best-effort — failures are logged but
 * not surfaced. The toggle UI updates the in-memory preference synchronously
 * via setPreference(), so a write failure is visible only after a restart.
 */
export async function saveThemePreference(preference: ThemePreference): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, preference);
  } catch (err) {
    console.warn('[theme] Failed to save theme preference:', err);
  }
}
